import { TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';

import Modal from '../../../components/general/modal';

export interface ReasonModalProps extends LocalizeContextProps {
    open: boolean;
    closeModal: () => void;
    // Resolves true when the mutation succeeded (so the modal closes) and false
    // on error (so it stays open with the entered values for a retry).
    onConfirm: (reason: string) => void | Promise<boolean>;
    loading: boolean;
    titleId: string;
    bodyId: string;
    reasonLabelId: string;
    reasonTestId: string;
    // Extra gate ANDed with the mandatory reason (e.g. add-service needs a
    // selected service too). Defaults to allowed.
    confirmDisabled?: boolean;
    // Optional content rendered above the reason field (e.g. a service picker).
    children?: React.ReactNode;
}

// SHER-366 — shared mandatory-reason confirm modal for the request-modification
// actions. A reason is always required; the confirm button is additionally
// disabled while a mutation is in flight (double-submit guard) and whenever the
// caller signals confirmDisabled. Reason state is local and reset on close.
export const ReasonModal: React.FC<ReasonModalProps> = ({ open, closeModal, onConfirm, loading, titleId, bodyId, reasonLabelId, reasonTestId, confirmDisabled, children, translate }) => {
    const [reason, setReason] = useState('');

    const close = () => { setReason(''); closeModal(); };
    const canConfirm = reason.trim().length > 0 && !confirmDisabled;

    const confirm = async () => {
        const succeeded = await onConfirm(reason.trim());
        // void return (e.g. stories) leaves the modal open; the containers
        // resolve true on success so the modal closes and clears its state.
        if (succeeded) {
            close();
        }
    };

    return (
        <Modal
            open={open}
            closeModal={close}
            confirmAction={canConfirm ? confirm : null}
            disabled={loading}
            fullWidth
            maxWidth="sm"
            title={<Translate id={titleId} />}
        >
            {/* SHER-363 #4: give the content a full-width column so the service
                picker + reason field use the dialog width instead of collapsing to
                a cramped column (the modal centers its content with flexbox). */}
            <div style={{ width: '100%' }}>
                <Typography gutterBottom>
                    <Translate id={bodyId} />
                </Typography>
                {children}
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    required
                    margin="normal"
                    label={translate(reasonLabelId) as string}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    inputProps={{ 'data-testid': reasonTestId }}
                />
            </div>
        </Modal>
    );
};

export const ReasonModalLocalized = withLocalize(ReasonModal);

export default ReasonModalLocalized;
